/**
 * Boundary Query Hooks
 *
 * React Query hooks for fetching area boundary data.
 */

import { useQuery } from '@zg-rentals/react-query5';
import type { UseQueryOptions } from '@zg-rentals/react-query5';
import { area, type EncodedBoundaryResponse } from '@/src/lib/api/endpoints/area';

// Query keys for type-safe cache management
export const boundaryKeys = {
  all: ['boundary'] as const,
  byId: (id: string) => [...boundaryKeys.all, 'byId', id] as const,
};

/**
 * Hook for fetching encoded boundary data by area ID
 *
 * @param areaId - Area ID to fetch boundary for
 * @param options - React Query options
 * @returns Query result with boundary data
 *
 * @example
 * ```tsx
 * const { data: boundaryData } = useEncodedBoundary(area.id);
 * ```
 */
export const useEncodedBoundary = (
  areaId: string | undefined,
  options?: Omit<UseQueryOptions<EncodedBoundaryResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    // Use unique query key per area ID to avoid cache collisions
    // When areaId is undefined, use a placeholder key that won't match any real area
    queryKey: areaId ? boundaryKeys.byId(areaId) : (['boundary', 'disabled'] as const),
    queryFn: async () => {
      if (!areaId) {
        throw new Error('Area ID is required for boundary fetch');
      }
      return await area.getEncodedBoundary(areaId);
    },
    enabled: !!areaId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    // Retry on failure to handle transient network errors
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    meta: {
      tags: ['boundary'],
    },
    ...options,
  });
};
